import { useState } from "react";
import { useNavigate } from "@/lib/router-compat";
import { MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { STATE_GRANTS } from "@/data/statesGrants";
import { trackEvent } from "@/lib/analytics";

const PARAMS = {
  assistance_type: "grant",
  income_band: "low",
  buyer_status: "first_time",
};

export default function GrantStateSelector() {
  const navigate = useNavigate();
  const [abbr, setAbbr] = useState<string>("");

  const selected = STATE_GRANTS.find((s) => s.abbr === abbr);

  const go = () => {
    if (!selected) return;
    trackEvent("state_selector_use", {
      entry_section: "state_selector",
      state_code: selected.abbr,
      assistance_type: PARAMS.assistance_type,
      repayment_type: "mixed",
    });
    trackEvent("matching_tool_start", {
      entry_section: "state_selector",
      state_code: selected.abbr,
      assistance_type: PARAMS.assistance_type,
      repayment_type: "mixed",
    });
    const qs = new URLSearchParams({
      state: selected.abbr,
      ...PARAMS,
    }).toString();
    navigate(`/grant-finder?${qs}`);
  };

  return (
    <Card className="border-primary/30 bg-primary/5 print:hidden">
      <CardContent className="p-5 sm:p-6">
        <p className="mb-1 font-bold text-foreground">Find low-income buyer programs in your state</p>
        <p className="mb-4 text-sm text-muted-foreground">
          Pick your state and we'll open the matching tool pre-set for first-time buyer assistance.
        </p>
        <div className="flex flex-col gap-3 sm:flex-row">
          <Select value={abbr} onValueChange={setAbbr}>
            <SelectTrigger className="sm:max-w-xs" aria-label="Select your state">
              <SelectValue placeholder="Select your state" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {STATE_GRANTS.map((s) => (
                <SelectItem key={s.abbr} value={s.abbr}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={go} disabled={!selected} className="shrink-0">
            <MapPin className="mr-2 h-4 w-4" aria-hidden />
            Show my programs
          </Button>
        </div>
        {selected && (
          <p className="mt-3 text-xs text-muted-foreground">
            {selected.name} programs are administered through {selected.hfa} ({selected.hfaShort}) plus city and
            county agencies.
          </p>
        )}
      </CardContent>
    </Card>
  );
}